import React, { useEffect, useState } from "react";
import { useAuth } from "../../components/AuthContext/AuthContext";
import { medicationFields } from "../../stories/Forms/fields";
import { medicalConditionsMedicationsSupplementsSchema } from "../../stories/Forms/validationSchema";
import { Spinner } from "react-bootstrap";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import useApis from "../../services/useAPI";
import {
  assessmentStatusUrl,
  assessmentValidateUrl,
  phlebotomyAssessmentStatusUrl,
} from "../../utils/constants";
import CheckboxGroupForm from "../../stories/Forms/CheckboxGroupForm";
import { XSquare } from "react-bootstrap-icons";
import { useStepDataProtection } from "./useStepDataProtection";

let submissionData;

/**
 * Medications Component
 * Handles the "Medications" step of the health assessment process.
 * Displays a form with checkboxes to capture medication information.
 * Validates user input and processes submission data.
 */

function Medications() {
  const auth = useAuth();
  const { bid, test_type } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = globalThis.location.pathname; // '/assessment/25/5'
  const parts = currentPath.split("/");
  const thisStep = parts[parts.length - 1]; // '5'
  localStorage.setItem("previous_step", thisStep);
  const formId = location.pathname.split("/").at(-1);
  const [is_assessment_completed, setIs_assessment_completed] = useState(false);
  const { setCompletedSteps, setIsCompleted, setStep } = auth;
  const { test_kit_id } = useParams();

  const [data, setData] = useState({});

  const isPhlebotomy = location.pathname.includes("boots");

  const { callApi, resultGet, resultPost, serverError, isLoading } = useApis();

  useStepDataProtection(
    auth?.allStepData,
    resultGet,
    test_kit_id,
    location,
    navigate,
    auth?.showToast
  );

  const handleAssessmentSubmit = (values) => {
    // Remove keys with undefined values
    Object.keys(values).forEach((key) => {
      if (values[key] === undefined) {
        delete values[key];
      }
    });

    const medications = values?.medications?.medications?.medications || [];
    const hasOtherMedications = medications?.includes("Other");
    const otherMedicationsDescription =
      values?.medications?.other_description || "";

    // Validate the 'Other' medications logic
    if (
      hasOtherMedications &&
      !otherMedicationsDescription &&
      values?.medication === "Yes"
    ) {
      return;
    }

    submissionData = {
      medications: {
        medications:
          values.medications?.medications?.medication === "Yes"
            ? medications
            : [], // List of medications
        other_description: hasOtherMedications
          ? otherMedicationsDescription
          : "", // Include description if 'Other' is selected
        details: hasOtherMedications ? otherMedicationsDescription : "",
      },
      step: "medications",
    };

    if (submissionData) {
      callApi(
        {
          user_id: JSON.parse(localStorage.getItem("user_id")),
          ...submissionData,
        },
        `${assessmentValidateUrl}`,
        "POST",
        true
      );
    }
    setData(submissionData);
    auth.setAllStepData((prevStep) => ({ ...prevStep, ...submissionData }));

    setIsCompleted(Number.parseInt(formId) + 1); // Increment the step based on the latest value
    setCompletedSteps((prevSteps) => prevSteps + 1); // functional update to access the latest value
    setStep((prevStep) => prevStep + 1);
  };

  useEffect(() => {
    if (resultGet) {
      auth?.setTotalSteps(
        resultGet?.list_of_steps?.includes("phlebotomist_booking_info") ? 3 : 4
      );

      const medications =
        auth?.allStepData?.medications?.medications ||
        resultGet?.assessment_values?.medications?.medications ||
        [];
      const hasOtherMedications = medications?.includes("Other");
      const otherMedicationsDescription =
        auth?.allStepData?.medications?.other_description ||
        resultGet?.assessment_values?.medications?.other_description ||
        ""; // Fetch from the correct path

      // Validate the 'Other' medications logic
      if (hasOtherMedications && !otherMedicationsDescription) {
        return; // Return early if validation fails
      }

      submissionData = {
        medications: {
          medications: medications, // List of medications
          other_description: hasOtherMedications
            ? otherMedicationsDescription
            : "", // Include description if 'Other' is selected
          details: hasOtherMedications ? otherMedicationsDescription : "",
        },
        step: "medications",
      };

      setData(submissionData);
    }
  }, [resultGet]);

  useEffect(() => {
    if (isPhlebotomy && test_type && bid) {
      callApi(null, phlebotomyAssessmentStatusUrl(bid), "GET");
    } else if (!isPhlebotomy && !test_type && !bid) {
      if(test_kit_id){
        localStorage.setItem("test_kit_id", location?.state?.test_kit_id);
      }
      callApi(null, `${assessmentStatusUrl}${test_kit_id}`, "GET", true);
    }
  }, []);

  useEffect(() => {
    if (resultPost) {
      setIsCompleted(Number.parseInt(formId));
      setCompletedSteps(Number.parseInt(formId) + 1); 
      setStep(Number.parseInt(formId));
      if (isPhlebotomy && test_type && bid) {
        navigate(
          `/bloods/boots/assessment/${test_type}/${bid}/${
            Number.parseInt(formId) + 2
          }`,
          { state: { ...auth.allStepData } }
        );
      } else {
        navigate(
          `/assessment/${test_kit_id}/${
            resultGet?.test_kit_details?.test_kit_type?.includes("sex") ||
            resultGet?.test_kit_details?.test_kit_type?.includes("menopause") ||
            resultGet?.test_kit_details?.test_kit_type?.includes("polycystic")
              ? Number.parseInt(formId) + 1
              : 7
          }`,
          { state: { ...auth.allStepData } }
        );
      }
    }
  }, [resultGet, resultPost]);

  useEffect(() => {
    setIs_assessment_completed(resultGet?.is_assessment_completed);
    if (serverError) {
      auth?.showToast("Error", serverError, "../assets/mhc.png", <XSquare />);
    }
  }, [resultGet, serverError]);

  if (isLoading) {
    return (
      <div className="text-center">
        <Spinner
          role="output"
          animation="border"
          variant="info"
          className="my-custom-spinner"
        />
      </div>
    );
  }

  return (
    <div className="mt-5">
      <h1 className="mb-4">Medication</h1>
      <CheckboxGroupForm
        className="assessment-form"
        initialValues={{ ...data?.medications }}
        blockRefresh={true}
        fields={medicationFields}
        validationSchema={medicalConditionsMedicationsSupplementsSchema}
        is_assessment_completed={resultGet?.is_assessment_completed}
        type={"medications"}
        onSubmit={handleAssessmentSubmit}
      />
    </div>
  );
}

export default Medications;
